import React from 'react';
import { View, TouchableOpacity, Dimensions, Animated } from 'react-native';
import { router } from 'expo-router';
import { Image } from 'expo-image';
import { Bookmark, Umbrella, Trees, TrafficCone, Mountain, Lock, Home, Users, Globe } from 'lucide-react-native';
import { DistanceIcon, TerrainIcon, RatingStarIcon } from '@/components/CardBadgeIcons';
import theme from '@/constants/colors';
import TrailMapPreview from '@/components/TrailMapPreview';
import TrailPathPreview from '@/components/TrailPathPreview';
import { LinearGradient } from 'expo-linear-gradient';
import { Trail } from '@/types/trail';
import { useDogs } from '@/context/DogsContext';
import { useSelectedDog } from '@/context/SelectedDogContext';
import styles from '@/components/TrailCard.styles';
import { Text } from '@/components';
import { formatDistance } from '@/utils/distance';
import { useTranslation } from 'react-i18next';

type Props = {
  trail: Trail;
  onPress?: (id: string) => void;
  onBookmarkPress?: (id: string) => void;
  isSaved?: boolean;
  onSwipeStateChange?: (id: string, swiping: boolean) => void;
  containerStyle?: any;
  imageStyle?: any;
  variant?: 'large' | 'vertical';
  /** Explore-like surfaces: public is the default there, so only the
   *  exceptions (private/family/pack) get a badge. */
  hidePublicBadge?: boolean;
};

export default function TrailCard({ trail, onPress, onBookmarkPress, isSaved, onSwipeStateChange, containerStyle, imageStyle, variant = 'large', hidePublicBadge = false }: Props) {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isSwiping, setIsSwiping] = React.useState(false);
  const [cardWidth, setCardWidth] = React.useState<number>(Dimensions.get('window').width);
  const anim = React.useRef(new Animated.Value(1)).current;
  const { dogProfiles } = useDogs();
  const { dogId } = useSelectedDog();

  // Avatar must match the dog whose match score the API returned, i.e. the
  // DogPicker selection (SelectedDogContext), not DogsContext's first dog.
  const dog = React.useMemo(
    () => (dogId ? dogProfiles.find((d) => d.id === dogId) : null) ?? dogProfiles[0] ?? null,
    [dogId, dogProfiles],
  );

  const images: string[] = React.useMemo(() => {
    if (Array.isArray((trail as any).images) && (trail as any).images.length > 0) {
      return (trail as any).images.map((i: any) => i?.url).filter(Boolean);
    }
    if (trail.photo) return [trail.photo];
    return [];
  }, [trail]);

  const isShowingImage = React.useMemo(() => {
    if (images.length > 0) return (activeIndex || 0) < images.length;
    return !!trail.photo;
  }, [images.length, activeIndex, trail.photo]);

  const distanceFromUserValue = React.useMemo(() => {
    return (trail as any).distanceFromUser ?? (trail as any).distance_from_user ?? (trail as any).distanceFromUserMeters ?? (trail as any).distance_from_user_meters;
  }, [trail]);

  const dogMatchValue = React.useMemo(() => {
    const v = (trail as any).dogMatchScore ?? (trail as any).dog_match_score ?? (trail as any).dogMatch;
    return typeof v === 'number' ? v : undefined;
  }, [trail]);

  // Visibility pill: Private 🔒 · Family/Pack 🏠👥 shared to one of the user's
  // groups (labelled with the group name) · Public 🌐 — visible on every card
  // in explore/created/saved/history so users know the audience before tapping.
  const visibilityBadge = React.useMemo(() => {
    const vis = String((trail as any).visibility ?? 'public').toLowerCase();
    const groupName: string | undefined = (trail as any).sharedGroupName ?? (trail as any).shared_group_name;
    const cfg: Record<string, { Icon: any; key: string; name?: string }> = {
      private: { Icon: Lock, key: 'private' },
      family: { Icon: Home, key: 'family', name: groupName },
      pack: { Icon: Users, key: 'pack', name: groupName },
      public: { Icon: Globe, key: 'public' },
    };
    const conf = cfg[vis] ?? cfg['public'];
    const label = t(`trailCard.${conf.key}`) + (conf.name ? ` · ${conf.name}` : '');
    // on explore-like surfaces public is assumed — only render the exceptions
    if (hidePublicBadge && vis === 'public') return null;
    return { conf, label, isPublic: vis === 'public' };
  }, [trail, t, hidePublicBadge]);

  const tagIcons = React.useMemo(() => {
    const tags: string[] = (trail as any).environment_tags || (trail as any).tags || [];
    if (!Array.isArray(tags) || tags.length === 0) return [] as React.ReactNode[];

    const mapKey = (t: string) => (t || '').toString().toLowerCase();
    const TAG_ICON_MAP: Record<string, any> = {
      beach: Umbrella,
      forest: Trees,
      road: TrafficCone,
      cliff: Mountain,
      mountain: Mountain,
    };

    return tags
      .map((t: string) => ({ raw: t, key: mapKey(t), Icon: TAG_ICON_MAP[mapKey(t)] }))
      .filter(x => x.Icon)
      .slice(0, 4)
      .map(x => (
        <View key={`tag-${String(x.raw)}`} style={styles.tagIcon}>
          <x.Icon size={14} color={theme.accentPrimary} strokeWidth={2} />
        </View>
      ));
  }, [trail]);

  const handleTouchStart = () => {
    setIsSwiping(true);
    onSwipeStateChange?.(trail.id, true);
  };

  const clearSwiping = (delay = 120) => {
    setTimeout(() => {
      setIsSwiping(false);
      onSwipeStateChange?.(trail.id, false);
    }, delay);
  };

  const triggerBookmarkAnim = () => {
    Animated.sequence([
      Animated.timing(anim, { toValue: 0.7, duration: 100, useNativeDriver: true }),
      Animated.spring(anim, { toValue: 1, friction: 4, tension: 120, useNativeDriver: true }),
    ]).start();
  };

  const handleBookmark = (e: any) => {
    e.stopPropagation();
    triggerBookmarkAnim();
    onBookmarkPress?.(trail.id);
  };

  if (variant === 'vertical') {
    return (
      <TouchableOpacity
        activeOpacity={0.95}
        onPress={() => { if (!isSwiping && onPress) onPress(trail.id); }}
        style={[styles.trailCard, styles.trailCardVertical, containerStyle]}
      >
        {trail.photo ? (
          <Image source={{ uri: trail.photo }} style={styles.trailImage} contentFit="cover" />
        ) : (
          <TrailMapPreview
            coordinates={trail.coordinates}
            path={trail.path}
            style={styles.trailImage}
            startLatitude={trail.startLatitude}
            startLongitude={trail.startLongitude}
          />
        )}

        <TouchableOpacity style={styles.bookmarkButtonVertical} onPress={handleBookmark}>
          <Animated.View style={{ transform: [{ scale: anim }] }}>
            <Bookmark size={20} color={isSaved ? '#000' : theme.textMuted} fill={isSaved ? '#000' : 'none'} strokeWidth={2} />
          </Animated.View>
        </TouchableOpacity>
        <View style={styles.trailContentBelow}>
          <Text style={styles.trailName}>{trail.name || `Trail ${new Date(trail.date).toLocaleDateString()}`}</Text>
          <Text style={styles.trailLocation}>{trail.city ? `${trail.city}, ${trail.country || t('trailDetail.unknown')}` : t('explore.locationUnknown')}</Text>
          <View style={styles.trailBadges}>
            {visibilityBadge && (() => { const { Icon } = visibilityBadge.conf; return (
              <View style={styles.trailBadge}>
                <Icon size={14} color={visibilityBadge.isPublic ? theme.textMuted : theme.accentSecondary} />
                <Text style={styles.trailBadgeText}>{visibilityBadge.label}</Text>
              </View>
            ); })()}
            {tagIcons}
            {trail.difficulty && (
              <View style={styles.trailBadge}>
                <TerrainIcon size={14} color={theme.accentSecondary} />
                <Text style={styles.trailBadgeText}>{trail.difficulty}</Text>
              </View>
            )}
            {Number.isFinite((trail as any).distance) && (
              <View style={styles.trailBadge}>
                <DistanceIcon size={14} color={theme.accentSecondary} />
                <Text style={styles.trailBadgeText}>{formatDistance((trail as any).distance)}</Text>
              </View>
            )}
            {Number.isFinite(distanceFromUserValue) && (
              <View style={styles.trailBadge}>
                <DistanceIcon size={14} color={theme.accentSecondary} />
                <Text style={styles.trailBadgeText}>{formatDistance(distanceFromUserValue as number)}</Text>
              </View>
            )}
            {Number.isFinite((trail as any).rating) && (
              <View style={styles.trailBadge}>
                <RatingStarIcon size={14} color={theme.accentSecondary} />
                <Text style={styles.trailBadgeText}>{(trail as any).rating.toFixed(1)}</Text>
              </View>
            )}
            {Number.isFinite(dogMatchValue) && (
              <TouchableOpacity style={styles.trailBadge} onPress={() => router.push(`/trail/${trail.id}/match`)}>
                <RatingStarIcon size={14} color={theme.accentSecondary} />
                <Text style={styles.trailBadgeText}>{`${Math.round(dogMatchValue as number)}%`}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity
      activeOpacity={0.95}
      delayPressIn={80}
      onPress={() => { if (!isSwiping && onPress) onPress(trail.id); }}
      style={[styles.largeTrailCard, containerStyle]}
    >
      <View
        style={[styles.largeTrailImageContainer, { width: '100%' }]}
        onLayout={(e) => setCardWidth(e.nativeEvent.layout.width || Dimensions.get('window').width)}
      >
        {/* Show only the first image (if available), remove side-scroll, make whole image area clickable */}
        {images.length > 0 ? (
          <Image
            key={`${trail.id}-img-0`}
            source={{ uri: images[0] }}
            style={[styles.largeTrailImage, { width: cardWidth }, imageStyle]}
            contentFit="cover"
          />
        ) : trail.photo ? (
          <Image source={{ uri: trail.photo }} style={[styles.largeTrailImage, { width: cardWidth }, imageStyle]} contentFit="cover" />
        ) : (
          <View style={[styles.largeTrailImage, { width: cardWidth }, imageStyle]}>
            <TrailMapPreview
              coordinates={trail.coordinates}
              path={trail.path}
              style={{ flex: 1 }}
              startLatitude={trail.startLatitude}
              startLongitude={trail.startLongitude}
            />
          </View>
        )}

        {(trail.path || trail.coordinates) && isShowingImage && (
          <View style={styles.rightOverlay} pointerEvents="none">
            <View style={styles.rightOverlayMap}>
              <LinearGradient
                colors={['transparent', 'rgba(0,0,0,0.55)', 'rgba(0,0,0,0.75)' ]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}
                pointerEvents="none"
              />
              <TrailPathPreview
                coordinates={trail.coordinates}
                path={trail.path}
                style={{ flex: 1 }}
                strokeColor="#ffffff"
                strokeWidth={2.3}
                backgroundColor={'transparent'}
              />
            </View>
          </View>
        )}

        {/* removed horizontal dots/indicator since side-scroll was removed */}

        {/* <TouchableOpacity style={styles.bookmarkButton} onPress={handleBookmark}>
          <Bookmark size={18} color={isSaved ? '#000' : theme.textMuted} />
        </TouchableOpacity> */}
      </View>

      <View style={styles.cardContent}>
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>{trail.name || `Trail ${new Date(trail.date).toLocaleDateString()}`}</Text>
          {/* <TouchableOpacity>
            <MoreVertical size={20} color={theme.textPrimary} />
          </TouchableOpacity> */}
        </View>

        <Text style={styles.cardLocation}>{Number.isFinite(distanceFromUserValue) ? formatDistance(distanceFromUserValue as number) : ''} {Number.isFinite(distanceFromUserValue) ? t('trailDetail.fromYou') : ''}</Text>

        <View style={styles.cardBadges}>
          {visibilityBadge && (() => { const { Icon } = visibilityBadge.conf; return (
            <View style={styles.badge}>
              <Icon size={14} color={visibilityBadge.isPublic ? theme.textMuted : theme.accentSecondary} />
              <Text style={styles.badgeText}>{visibilityBadge.label}</Text>
            </View>
          ); })()}
          {Number.isFinite(dogMatchValue) && (
            <TouchableOpacity style={styles.badge} onPress={() => router.push(`/trail/${trail.id}/match`)}>
              <Image source={{ uri: dog?.image }} style={{ width: 14, height: 14, borderRadius: 7 }} contentFit="cover" />
              <Text style={styles.badgeText}>{`${Math.round(dogMatchValue as number)}%`}</Text>
            </TouchableOpacity>
          )}
          {Number.isFinite((trail as any).distance) && (
            <View style={styles.badge}>
              <DistanceIcon size={14} color={theme.accentSecondary} />
              <Text style={styles.badgeText}>{formatDistance((trail as any).distance)}</Text>
            </View>
          )}
          {trail.difficulty && (
            <View style={styles.badge}>
              <TerrainIcon size={14} color={theme.accentSecondary} />
              <Text style={styles.badgeText}>{trail.difficulty}</Text>
            </View>
          )}
          {Number.isFinite((trail as any).rating) && (
            <View style={styles.badge}>
              <RatingStarIcon size={14} color={theme.accentSecondary} />
              <Text style={styles.badgeText}>{(trail as any).rating.toFixed(1)}</Text>
            </View>
          )}
          
        </View>
      </View>
    </TouchableOpacity>
  );
}
